import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { get } from "../../lib/request"
import {
  buildMessagesReportFetchKey,
  formatPostHeatmapReportDate,
} from "../../lib/utils/messagesReport"
import { normalizePulseApiPayload } from "../../lib/utils/pulseApiRows"
import { skipIfFetchKeyLoaded } from "../utils/fetchDedupe"

/**
 * GET /api/v1/report/clients/{clientId}/heatmap?start_date=&end_date=
 */
export const fetchClientPostHeatmap = createAsyncThunk(
  "clientPostHeatmap/fetch",
  async ({ clientId, startDate, endDate } = {}, { rejectWithValue }) => {
    try {
      if (clientId == null || clientId === "") {
        return rejectWithValue("clientId is required")
      }
      if (!startDate || !endDate) {
        return rejectWithValue("startDate and endDate are required")
      }

      const start = formatPostHeatmapReportDate(startDate)
      const end = formatPostHeatmapReportDate(endDate)
      const query = new URLSearchParams({ start_date: start, end_date: end }).toString()
      const data = await get(`report/clients/${clientId}/heatmap?${query}`)

      return {
        rows: normalizePulseApiPayload(data),
        clientId: String(clientId),
        startDate: start,
        endDate: end,
        fetchKey: buildMessagesReportFetchKey(clientId, start, end),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch post heatmap")
    }
  },
  {
    condition: (arg, { getState }) => {
      const { clientId, startDate, endDate } = arg || {}
      const start = formatPostHeatmapReportDate(startDate)
      const end = formatPostHeatmapReportDate(endDate)
      const key = buildMessagesReportFetchKey(clientId, start, end)
      return !skipIfFetchKeyLoaded(getState().clientPostHeatmap, key)
    },
  },
)

const initialState = {
  rows: [],
  status: "idle",
  error: null,
  requestedFetchKey: null,
  fetchedFetchKey: null,
  clientId: null,
  startDate: null,
  endDate: null,
}

function beginFetch(state, action) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatPostHeatmapReportDate(startDate)
  const end = formatPostHeatmapReportDate(endDate)
  state.status = "loading"
  state.error = null
  state.requestedFetchKey = buildMessagesReportFetchKey(clientId, start, end)
  state.fetchedFetchKey = null
  state.rows = []
}

function isStaleFetch(action, state) {
  const { clientId, startDate, endDate } = action.meta.arg || {}
  const start = formatPostHeatmapReportDate(startDate)
  const end = formatPostHeatmapReportDate(endDate)
  const key = buildMessagesReportFetchKey(clientId, start, end)
  return key !== String(state.requestedFetchKey ?? "")
}

const clientPostHeatmapSlice = createSlice({
  name: "clientPostHeatmap",
  initialState,
  reducers: {
    clearClientPostHeatmap: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientPostHeatmap.pending, (state, action) => {
        beginFetch(state, action)
      })
      .addCase(fetchClientPostHeatmap.fulfilled, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "succeeded"
        state.rows = action.payload?.rows ?? []
        state.clientId = action.payload?.clientId ?? null
        state.startDate = action.payload?.startDate ?? null
        state.endDate = action.payload?.endDate ?? null
        state.fetchedFetchKey = action.payload?.fetchKey ?? null
      })
      .addCase(fetchClientPostHeatmap.rejected, (state, action) => {
        if (isStaleFetch(action, state)) return
        state.status = "failed"
        state.error = action.payload ?? "Failed to fetch post heatmap"
        state.rows = []
        state.fetchedFetchKey = state.requestedFetchKey
      })
  },
})

export const { clearClientPostHeatmap } = clientPostHeatmapSlice.actions
export default clientPostHeatmapSlice.reducer
